import React, { useState, useEffect, useReducer } from "react";
import Swal from "sweetalert2";
import Select from "react-select";
import { Collapse } from "react-bootstrap";
import { Link } from "react-router-dom";
import ProductServices from "../../../services/ProductServices";
import CategoryServices from "../../../services/CategoryServices";
import UnitConversionService from "../../../services/UnitConversionService";

const initialState = {
    openCollapse1: true,
    openCollapse2: true,
};

const reducer = (state, action) => {
    switch (action.type) {
        case "openCollapse1":
            return { ...state, openCollapse1: !state.openCollapse1 };
        case "openCollapse2":
            return { ...state, openCollapse2: !state.openCollapse2 };
        default:
            return state;
    }
};

const ConversionUnit = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [conversions, setConversions] = useState([]);
    const [units, setUnits] = useState([]);
    const [masterParts, setMasterParts] = useState([]);
    const [formValues, setFormValues] = useState({
        masterPart: "",
        fromUnit: "",
        toUnit: "",
        conversionValue: "",
    });
    const [reload, setReload] = useState(0);
    const [loading, setLoading] = useState(false);

    // ✅ Fetch master parts & units
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [unitRes, masterRes, convRes] = await Promise.all([
                    CategoryServices.getUnit(),
                    ProductServices.getAllProduct(),
                    UnitConversionService.getAllUnit(),
                ]);

                setUnits(unitRes?.data?.data || []);
                setMasterParts(masterRes?.data?.data || []);
                setConversions(convRes?.data?.data || []);
            } catch (error) {
                console.error("Error loading data:", error);
                Swal.fire("Error", "Failed to load data from server", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [reload]);

    const masterPartOptions = masterParts.map((part) => ({
        value: part._id,
        label: `${part.partNo} - ${part.description}`,
        partNo: part.partNo,
        description: part.description,
        unit: part.unit
    }));

    // ✅ Handle input changes
    const handleInputChange = (event) => {
        const { name, value } = event.target;

        if (name === "masterPart") {
            // Find selected master part from list
            const selectedMaster = masterParts.find((p) => p._id === value);

            // Get its fromUnit (the one in masterList)
            const fromUnitId = selectedMaster?.unit?._id || "";

            // Auto-set fromUnit and make it locked
            setFormValues((prev) => ({
                ...prev,
                masterPart: value,
                fromUnit: fromUnitId,
            }));

            return;
        }

        setFormValues({ ...formValues, [name]: value });
    };


    // ✅ Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        const { masterPart, fromUnit, toUnit, conversionValue } = formValues;

        // 🧠 Validation
        if (!masterPart || !fromUnit || !toUnit || !conversionValue) {
            return Swal.fire({
                icon: "error",
                title: "Missing Fields",
                text: "Please fill all fields before saving!",
            });
        }

        if (fromUnit === toUnit) {
            return Swal.fire({
                icon: "error",
                title: "Invalid Conversion",
                text: "From Unit and To Unit cannot be the same!",
            });
        }

        try {
            const res = await UnitConversionService.createConversion(formValues);
            console.log("res", res)

            // ✅ Check HTTP status instead of nested data
            if (res.status === 201) {
                await Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: res.message || "Unit Conversion Added Successfully!",
                    timer: 1500,
                    showConfirmButton: false,
                });

                // ✅ Reset form fields
                setFormValues({
                    masterPart: "",
                    fromUnit: "",
                    toUnit: "",
                    conversionValue: "",
                });

                // ✅ Trigger useEffect to refresh data
                setReload((prev) => prev + 1);
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Failed",
                    text: "Unexpected server response.",
                });
            }
        } catch (error) {
            console.error("Conversion creation error:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    "Failed to save conversion rule. Please try again.",
            });
        }
    };


    // ✅ Handle delete
    const handleDelete = (conversion) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this conversion rule!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#aaa",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                UnitConversionService.deleteConversion(conversion._id)
                    .then(() => {
                        Swal.fire("Deleted!", "Conversion rule deleted successfully.", "success");
                        setReload((prev) => prev + 1);
                    })
                    .catch(() => {
                        Swal.fire("Error!", "Failed to delete the conversion rule.", "error");
                    });
            }
        });
    };

    return (
        <>
            <div className="row">
                <div className="col-xl-12">
                    <div className="row page-titles">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="#">Master-List</Link>
                            </li>
                            <li className="breadcrumb-item active">Unit Conversion</li>
                        </ol>
                    </div>

                    <div className="row">
                        {/* ➕ Add Conversion */}
                        <div className="col-xl-4">
                            <div className="filter cm-content-box box-primary">
                                <div className="content-title">
                                    <div className="cpa">
                                        <i className="fa-solid fa-sync me-1"></i>Add Conversion
                                    </div>
                                    <div className="tools">
                                        <Link
                                            to="#"
                                            className={`SlideToolHeader ${state.openCollapse1 ? "collapse" : "expand"
                                                }`}
                                            onClick={() => dispatch({ type: "openCollapse1" })}
                                        >
                                            <i className="fas fa-angle-up" />
                                        </Link>
                                    </div>
                                </div>

                                <Collapse in={state.openCollapse1}>
                                    <div className="cm-content-body form excerpt">
                                        <div className="card-body">
                                            <form onSubmit={handleSubmit}>
                                                <div className="mb-3">
                                                    <label className="form-label">Master Part</label>

                                                    <Select
                                                        options={masterPartOptions}
                                                        placeholder="Search Part No or Description..."
                                                        value={
                                                            masterPartOptions.find(
                                                                (o) => o.value === formValues.masterPart
                                                            ) || null
                                                        }
                                                        onChange={(selected) => {
                                                            if (!selected) {
                                                                setFormValues((prev) => ({
                                                                    ...prev,
                                                                    masterPart: "",
                                                                    fromUnit: ""
                                                                }));
                                                                return;
                                                            }

                                                            setFormValues((prev) => ({
                                                                ...prev,
                                                                masterPart: selected.value,
                                                                fromUnit: selected.unit?._id || ""
                                                            }));
                                                        }}
                                                        isClearable
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">From Unit</label>
                                                    <select
                                                        name="fromUnit"
                                                        className="form-control"
                                                        value={formValues.fromUnit}
                                                        disabled // 🔒 make it read-only
                                                    >
                                                        <option value="">Select From Unit</option>
                                                        {units.map((unit) => (
                                                            <option key={unit._id} value={unit._id}>
                                                                {unit.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>


                                                <div className="mb-3">
                                                    <label className="form-label">To Unit</label>
                                                    <select
                                                        name="toUnit"
                                                        className="form-control"
                                                        value={formValues.toUnit}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select To Unit</option>
                                                        {units.map((unit) => (
                                                            <option key={unit._id} value={unit._id}>
                                                                {unit.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <div className="mb-3">
                                                    <label className="form-label">Conversion Value</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="conversionValue"
                                                        value={formValues.conversionValue}
                                                        onChange={handleInputChange}
                                                        placeholder="e.g. 1000"
                                                    />
                                                </div>

                                                <button type="submit" className="btn btn-primary">
                                                    Save Conversion
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                        </div>

                        {/* 📜 Conversion List */}
                        <div className="col-xl-8">
                            <div className="filter cm-content-box box-primary">
                                <div className="content-title">
                                    <div className="cpa">
                                        <i className="fa-solid fa-list me-1"></i>Conversion List
                                    </div>
                                    <div className="tools">
                                        <Link
                                            to="#"
                                            className={`SlideToolHeader ${state.openCollapse2 ? "collapse" : "expand"
                                                }`}
                                            onClick={() => dispatch({ type: "openCollapse2" })}
                                        >
                                            <i className="fas fa-angle-up" />
                                        </Link>
                                    </div>
                                </div>

                                <Collapse in={state.openCollapse2}>
                                    <div className="cm-content-body publish-content form excerpt">
                                        <div className="card-body">
                                            {loading ? (
                                                <div className="text-center p-2">Loading...</div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Master Part</th>
                                                                <th>From Unit</th>
                                                                <th>To Unit</th>
                                                                <th>Value</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {conversions.length > 0 ? (
                                                                conversions.map((c, i) => (
                                                                    <tr key={c._id}>
                                                                        <td>{i + 1}</td>
                                                                        <td>
                                                                            {c.masterPart?.partNo} -{" "}
                                                                            {c.masterPart?.description}
                                                                        </td>
                                                                        <td>{c.fromUnit?.name}</td>
                                                                        <td>{c.toUnit?.name}</td>
                                                                        <td>{c.conversionValue}</td>
                                                                        <td>
                                                                            <button
                                                                                className="btn btn-danger btn-xs sharp"
                                                                                onClick={() => handleDelete(c)}
                                                                            >
                                                                                <i className="fa fa-trash"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center">
                                                                        No Conversion Found
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Collapse>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConversionUnit;
